import { t } from "@/i18n";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { BookOpen, Coins, LogOut, Paintbrush, Sparkles, FolderKanban, Sticker } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Loader";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const ModeLink = ({ to, icon: Icon, label, testid, color }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-bold text-sm sm:text-base transition-colors duration-150 ${
        isActive ? `${color} text-[#1E293B] shadow-[0_3px_0_0_rgba(30,41,59,0.2)]` : "text-slate-500 hover:text-[#1E293B]"
      }`
    }
  >
    <Icon className="w-4 h-4" /> {label}
  </NavLink>
);

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `hidden md:flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-sm transition-colors duration-150 ${
        isActive ? "text-[#FF5252] bg-red-50" : "text-slate-600 hover:text-[#1E293B] hover:bg-slate-100"
      }`
    }
  >
    <Icon className="w-4 h-4" /> {label}
  </NavLink>
);

export const Header = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b-2 border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
        <Link to={user ? "/create" : "/"} data-testid="header-logo-link"><Logo small /></Link>
        {user && (
          <div className="flex items-center bg-[#F3EFE6] p-1.5 rounded-2xl border-2 border-slate-200" data-testid="mode-switcher">
            <ModeLink to="/create" icon={Sparkles} label={t("create")} testid="mode-create-tab" color="bg-[#FFD166]" />
            <ModeLink to="/colour" icon={Paintbrush} label={t("colour")} testid="mode-colour-tab" color="bg-[#4ECDC4]" />
          </div>
        )}
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <NavItem to="/library" icon={BookOpen} label={t("library")} testid="nav-library-link" />
              <NavItem to="/packets" icon={FolderKanban} label={t("packets")} testid="nav-packets-link" />
              <Link to="/library?tab=stickers" className="sticker-badge hover:bg-pink-200 transition-colors" data-testid="sticker-count-display" title={t("my_sticker_shelf")}>
                <Sticker className="w-4 h-4" /> <span data-testid="sticker-count-value">{user.stickers?.length || 0}</span>
              </Link>
              <Link to="/credits" className="credit-badge hover:bg-amber-200 transition-colors" data-testid="credit-balance-display">
                <Coins className="w-4 h-4" /> <span data-testid="credit-balance-value">{user.credits}</span>
              </Link>
              <LanguageSwitcher compact />
              <button
                data-testid="logout-button"
                onClick={async () => { await logout(); nav("/"); }}
                className="p-2.5 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-[#1E293B] transition-colors"
                title={t("log_out")}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <LanguageSwitcher compact />
              <Link to="/login" data-testid="header-login-link" className="font-bold px-4 py-2 text-slate-700 hover:text-[#FF5252] transition-colors">{t("log_in")}</Link>
              <Link to="/signup" data-testid="header-signup-link" className="btn-pop btn-red !py-2.5 !px-5 text-sm">{t("start_free")}</Link>
            </>
          )}
        </div>
      </div>
      {user && (
        <div className="md:hidden flex justify-center gap-2 pb-2">
          <Link to="/library" className="text-sm font-bold text-slate-600 px-3 py-1" data-testid="mobile-nav-library">{t("library")}</Link>
          <Link to="/packets" className="text-sm font-bold text-slate-600 px-3 py-1" data-testid="mobile-nav-packets">{t("packets")}</Link>
        </div>
      )}
    </header>
  );
};
